import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { type CalendarApi } from "@fullcalendar/core"
import { Button, Flex, Typography } from "antd"
import dayjs from "dayjs"
import React, { useMemo } from "react"

const { Title } = Typography

interface CalendarNavigationProps {
  getAPI: () => CalendarApi | null | undefined
  isMobile: boolean
  currentView: string
  timezone: string
  onViewChange: (view: string) => void
  onTodayClick: () => void
  onPrevClick: () => void
  onNextClick: () => void
}

export function CalendarNavigation({
  getAPI,
  isMobile,
  currentView,
  timezone,
  onViewChange,
  onTodayClick,
  onPrevClick,
  onNextClick
}: CalendarNavigationProps) {
  // Availability isn't a real FullCalendar view (see Calendar.tsx), so its
  // title can't come from the hidden underlying view's own range — it always
  // reads as a single day, whatever the underlying calendar's date is.
  const title =
    currentView === "availability"
      ? dayjs(getAPI()?.getDate()).tz(timezone).format("dddd, MMMM D, YYYY")
      : getAPI()?.view?.title || "Loading..."

  // Mobile keeps its existing, simpler three-way switch (FullCalendar's list
  // views render better than timeGrid at that width); desktop gets the full
  // Day/Work Week/Week/Month/Schedule set from the Calendar 2.0 mockup.
  const steps = useMemo(() => {
    if (isMobile) {
      return [
        { key: "listDay", label: "Day" },
        { key: "listWeek", label: "Week" },
        { key: "listMonth", label: "Month" }
      ]
    }

    return [
      { key: "timeGridDay", label: "Day" },
      { key: "timeGridWorkWeek", label: "Work Week" },
      { key: "timeGridWeek", label: "Week" },
      { key: "dayGridMonth", label: "Month" },
      { key: "listWeek", label: "Schedule" },
      { key: "availability", label: "Availability" }
    ]
  }, [isMobile])

  return (
    <Flex
      wrap
      gap={"middle"}
      className={"pb-2"}
      align={"center"}
      justify={"space-between"}
    >
      <Flex wrap gap={"middle"} align={"center"}>
        <Flex gap={4} align={"center"}>
          <Button
            type={"text"}
            aria-label={"Previous period"}
            title={"Previous period"}
            icon={<LeftOutlined className={"font-bold stroke-3"} />}
            onClick={onPrevClick}
          />
          <Title className={"mb-0 min-w-max"} level={4}>
            {title}
          </Title>
          <Button
            type={"text"}
            aria-label={"Next period"}
            title={"Next period"}
            icon={<RightOutlined />}
            onClick={onNextClick}
          />
        </Flex>
        <Button className={"font-medium"} onClick={onTodayClick}>
          Today
        </Button>
      </Flex>

      <Flex gap={2} wrap className={"bg-[#F3F4F6] rounded-md p-0.5"}>
        {steps.map(({ key, label }) => (
          <Button
            key={key}
            className={
              currentView === key
                ? "shadow-sm font-medium"
                : "shadow-none border-transparent bg-transparent text-[#6B7280]"
            }
            onClick={() => onViewChange(key)}
            type={currentView === key ? "default" : "text"}
          >
            {label}
          </Button>
        ))}
      </Flex>
    </Flex>
  )
}
